import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { CloseIcon, ExternalLinkIcon } from "../../Icons";
import {
  MODAL_BACKDROP,
  MODAL_PANEL,
  MODAL_HEADER,
  MODAL_TITLE,
  BTN_SECONDARY,
} from "../../ui/pageChrome";

/**
 * In-app YouTube player. Opens embed; also offers open-on-YouTube.
 */
const TrainingVideoPlayerModal = ({ open, video, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !video) return null;

  const embedUrl =
    video.embedUrl ||
    (video.youtubeId
      ? `https://www.youtube.com/embed/${video.youtubeId}?rel=0&autoplay=1`
      : null);
  const watchUrl =
    video.watchUrl ||
    (video.youtubeId
      ? `https://www.youtube.com/watch?v=${video.youtubeId}`
      : video.youtubeUrl);

  return createPortal(
    <div
      className={MODAL_BACKDROP}
      role="dialog"
      aria-modal="true"
      aria-labelledby="training-player-title"
      onClick={onClose}
    >
      <div
        className={`${MODAL_PANEL} w-full max-w-4xl overflow-hidden shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={MODAL_HEADER}>
          <div className="min-w-0 flex-1 pr-2">
            <h3 id="training-player-title" className={MODAL_TITLE}>
              {video.title}
            </h3>
            {video.categoryLabel || video.durationLabel ? (
              <p className="mt-0.5 text-xs text-slate-500 truncate">
                {[video.categoryLabel, video.durationLabel]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close player"
          >
            <CloseIcon size={18} />
          </button>
        </div>

        <div className="bg-black aspect-video w-full">
          {embedUrl ? (
            <iframe
              title={video.title}
              src={`${embedUrl}${embedUrl.includes("?") ? "&" : "?"}autoplay=1`}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-slate-300 px-4 text-center">
              This video link could not be embedded. Open it on YouTube instead.
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 border-t border-slate-100 bg-white">
          <p className="text-xs text-slate-600 min-w-0 flex-1 leading-relaxed line-clamp-2">
            {video.description || "Training video"}
          </p>
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${BTN_SECONDARY} shrink-0`}
          >
            <ExternalLinkIcon size={14} />
            Open on YouTube
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TrainingVideoPlayerModal;
